/**
 * Local consent log — RGPD proof of consent (or refusal).
 *
 * Each time the user submits the cookie banner (accept all, refuse optional,
 * or save custom preferences), we append an immutable entry to
 * `localStorage[CONSENT_LOG_KEY]`. The log lives entirely in the browser:
 * Thiia never receives nor stores it server-side. The user can consult or
 * export it from the Privacy page.
 *
 * Cap the log to MAX_ENTRIES to bound localStorage usage; older entries are
 * dropped FIFO. The current consent state remains canonical in
 * `thiia_cookie_consent` — the log is a history.
 */

export const CONSENT_LOG_KEY = "thiia_cookie_consent_log";
const MAX_ENTRIES = 50;

export type ConsentDecision = "accept_all" | "refuse_optional" | "custom";

export interface ConsentLogEntry {
  /** ISO 8601 timestamp of the user's decision. */
  decidedAt: string;
  /** Schema version of the consent payload (matches `thiia_cookie_consent.version`). */
  version: string;
  /** Which button the user clicked. */
  decision: ConsentDecision;
  /** Resolved preferences after the click. */
  prefs: {
    essential: true;
    security: boolean;
    functional: boolean;
    analytics: boolean;
  };
  /** Best-effort — the page that hosted the banner (no PII). */
  page?: string;
  /** Best-effort — `navigator.userAgent` for forensic context. */
  userAgent?: string;
}

export function readConsentLog(): ConsentLogEntry[] {
  try {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(CONSENT_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ConsentLogEntry[]) : [];
  } catch {
    return [];
  }
}

export function appendConsentLog(entry: ConsentLogEntry): void {
  try {
    if (typeof window === "undefined") return;
    const current = readConsentLog();
    const next = [...current, entry];
    // FIFO cap.
    const trimmed = next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
    window.localStorage.setItem(CONSENT_LOG_KEY, JSON.stringify(trimmed));
    window.dispatchEvent(new CustomEvent("thiia:cookie-consent-log"));
  } catch {
    /* never block UX on logging */
  }
}

export function clearConsentLog(): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(CONSENT_LOG_KEY);
    window.dispatchEvent(new CustomEvent("thiia:cookie-consent-log"));
  } catch {
    /* noop */
  }
}

/** Trigger a JSON download of the full log for the user's records. */
export function downloadConsentLog(): void {
  try {
    const log = readConsentLog();
    const blob = new Blob([JSON.stringify(log, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `thiia-consent-log-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    /* noop */
  }
}
